import React, { useState, useEffect } from 'react';
import { Search } from "lucide-react";

const FormTable = () => {
    const [data, setData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(100);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchData = async (page = 1, firstName = '') => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                paginated: true,
                page_size: pageSize,
                page: page
            });

            const response = await fetch(`https://qa-getnet.recibepagos.com/nuestros-productos/bySantander/api/form?${params}`);
            if (!response.ok) {
                throw new Error('Error al obtener los datos');
            }
            const result = await response.json();
            console.log(result);

            const records = result.data.data.map(item => ({
                fecha: formatDate(item.creation_date),
                agente: item.agent || '',
                producto: item.product || '',
                nombre: item.first_name || '',
                apellido: item.last_name || '',
                correo: item.email || '',
                telefono: item.phone || '',
                santander: item.is_santander_client ? 'Si' : 'No',
                rfc: item.affiliation_nb || '',
                giro: item.sector_name || '',
            }));
            console.log(records);

            setData(records);
            setTotalItems(result.data.total);
            setTotalPages(result.data.total_pages);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const options = {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: 'America/Mexico_City'
        };
        return new Date(dateString).toLocaleString('es-MX', options);
    };

    useEffect(() => {
        fetchData(currentPage, searchTerm);
    }, [currentPage, pageSize]);

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        setCurrentPage(1);
        fetchData(1, value);
    };

    const exportCSV = () => {
        if (data.length === 0) return;
    
        const headers = Object.keys(data[0]).join(',');
        const rows = data
            .map(row => Object.values(row).map(val => `"${val}"`).join(','))
            .join('\n');
    
        const blob = new Blob([`${headers}\n${rows}`], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'informacion.csv';
        a.click();
        window.URL.revokeObjectURL(url);
    };
    

    if (loading) {
        return <div className="p-6 text-center">Cargando datos...</div>;
    }

    if (error) {
        return <div className="p-6 text-center text-red-500">{error}</div>;
    }

    return (
        <div className="p-6">
            <div className="flex items-center mb-4">
                <div className="flex items-center bg-[#D9D9D9] rounded overflow-hidden">
                    <input
                        type="text"
                        placeholder="Buscar por nombre"
                        className="bg-[#D9D9D9] text-black px-4 py-2 w-64 focus:outline-none rounded-l"
                        value={searchTerm}
                        onChange={handleSearch}
                    />
                    <div className="px-3 border-l border-[#D9D9D9]">
                        <Search size={18} className="text-black" />
                    </div>
                </div>
            </div>
            <div className="overflow-y-auto max-h-[400px] border border-gray-300 rounded">
                <table className="min-w-full">
                    <thead className="bg-gray-100">
                        <tr>
                            {['Fecha', 'Agente', 'Producto', 'Nombre', 'Apellido', 'Correo', 'Teléfono', 'Santander', 'RFC', 'Giro/Sector'].map((header, index) => (
                                <th key={index} className="px-4 py-2 text-left border-b border-gray-300">
                                    {header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.length > 0 ? data.map((row, i) => (
                            <tr key={i} className="hover:bg-gray-50">
                                <td className="px-4 py-2 border-b">{row.fecha}</td>
                                <td className="px-4 py-2 border-b">{row.agente}</td>
                                <td className="px-4 py-2 border-b">{row.producto}</td>
                                <td className="px-4 py-2 border-b">{row.nombre}</td>
                                <td className="px-4 py-2 border-b">{row.apellido}</td>
                                <td className="px-4 py-2 border-b">{row.correo}</td>
                                <td className="px-4 py-2 border-b">{row.telefono}</td>
                                <td className="px-4 py-2 border-b">{row.santander}</td>
                                <td className="px-4 py-2 border-b">{row.rfc}</td>
                                <td className="px-4 py-2 border-b">{row.giro}</td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="10" className="px-4 py-4 text-center text-gray-500">
                                    No se encontraron resultados
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="flex justify-between items-center mt-4">
                <button
                    onClick={exportCSV}
                    className="bg-gray-800 text-white px-6 py-2 rounded"
                >
                    Exportar a CSV
                </button>

                <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <span>
                        Página {currentPage} de {totalPages}
                    </span>
                    <button
                        onClick={() => {
                            if (currentPage > 1) {
                                const newPage = currentPage - 1;
                                setCurrentPage(newPage);
                                fetchData(newPage, searchTerm);
                            }
                        }}
                        disabled={currentPage === 1}
                        className="px-2 py-1 rounded hover:bg-gray-200 disabled:text-gray-400"
                    >
                        {'<'}
                    </button>
                    <span className="font-medium">{currentPage}</span>
                    <button
                        onClick={() => {
                            if (currentPage < totalPages) {
                                const newPage = currentPage + 1;
                                setCurrentPage(newPage);
                                fetchData(newPage, searchTerm);
                            }
                        }}
                        disabled={currentPage === totalPages}
                        className="px-2 py-1 rounded hover:bg-gray-200 disabled:text-gray-400"
                    >
                        {'>'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FormTable;
